/**
 * 视频创作管理器
 * 整合文本输入、演员选择、视频生成和结果管理的完整流程
 * @author 杨俊朗
 */
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { creationProductHooks } from "@/manager";
import { videoVideosByFolderProject } from "@/services/video-videos-by-folder-project";
import { videoAudioVideoProcessBatch } from "@/services/video-audio-video-process-batch";
import { videoRetryFailedVideo } from "@/services/video-retry-failed-video";
import { videoDelete } from "@/services/video-delete";
import { videoCallbackResult } from "@/services/video-callback-result";
import { useToast } from "@/hooks/use-toast";
import { canUseLocalStorage } from "@/utils/canuseLocalStorage";
import { canUseWindow } from "@/utils/canuseWindow";
import analytics from "@/utils/analytics";
import { SPEND_CREDITS_EVENT, subscriptionHook,  } from "./subscription-manager";
import { calculateCharacters } from "@/utils/calculate-characters";

// 事件类型定义
type VideoCreationScriptChangeEventDetail = {
  projectId: string;
  text: string;
};

type VideoCreationScriptChangeEvent =
  CustomEvent<VideoCreationScriptChangeEventDetail>;

// 事件类型定义
type VideoCreationActorsChangeEventDetail = {
  projectId: string;
  actors: TalkingActor[];
};

type VideoCreationActorsChangeEvent =
  CustomEvent<VideoCreationActorsChangeEventDetail>;

// 事件类型定义
type VideoCreationResultsChangeEventDetail = {
  projectId: string;
  results: VideoResult[];
};

type VideoCreationResultsChangeEvent =
  CustomEvent<VideoCreationResultsChangeEventDetail>;

// 项目缓存类型
interface ProjectVideoCreationCache {
  // 输入数据
  text: string;
  actors: TalkingActor[];
  // 结果数据
  results: VideoResult[];
  tempResults: { [key: string]: VideoResult };
}

export class CreationVideoManager {
  private static instance: CreationVideoManager;
  private readonly STORAGE_KEY = "MIUTA_VIDEO_CREATION";

  // 项目缓存
  private projectCache: { [projectId: string]: ProjectVideoCreationCache } = {};
  // 轮询
  private polling: NodeJS.Timeout | null = null;
  // 视频轮询映射
  private generatorPollingMap: { [taskId: string]: NodeJS.Timeout } = {};

  private constructor() {
    this.load();
  }

  public static getInstance(): CreationVideoManager {
    if (!CreationVideoManager.instance) {
      CreationVideoManager.instance = new CreationVideoManager();
    }
    return CreationVideoManager.instance;
  }

  // ==================== 输入管理 ====================

  public updateText(text: string, projectId: string): void {
    this.init(projectId);
    this.projectCache[projectId] = {
      ...this.projectCache[projectId],
      text,
    };
    this.dispatchScriptChange(projectId);
    this.save();
  }

  public getText(projectId: string): string {
    this.init(projectId);
    return this.projectCache[projectId].text || "";
  }

  public updateActors(actors: TalkingActor[], projectId: string): void {
    this.init(projectId);
    this.projectCache[projectId] = {
      ...this.projectCache[projectId],
      actors,
    };
    this.dispatchActorsChange(projectId);
    this.save();
  }

  public getActors(projectId: string): TalkingActor[] {
    this.init(projectId);
    return this.projectCache[projectId].actors || [];
  }

  // ==================== 结果管理 ====================

  // 更新结果
  private updateResults(results: VideoResult[], projectId: string): void {
    this.init(projectId);
    this.projectCache[projectId] = {
      ...this.projectCache[projectId],
      results,
    };
    this.dispatchResultsChange(projectId);
    this.save();
  }

  // 新增结果
  private addResults(results: VideoResult[], projectId: string): void {
    this.init(projectId);
    this.projectCache[projectId] = {
      ...this.projectCache[projectId],
      results: [...results, ...this.projectCache[projectId].results],
    };
    this.dispatchResultsChange(projectId);
    this.save();
  }

  // 获取结果
  public getResults(projectId: string): VideoResult[] {
    this.init(projectId);
    const permanentResults = this.projectCache[projectId].results || [];
    const tempResults = Object.values(
      this.projectCache[projectId].tempResults || {}
    );
    return [...tempResults, ...permanentResults];
  }

  // 删除结果
  public async removeResult(projectId: string, videoId: string): Promise<void> {
    this.init(projectId);
    const results = this.projectCache[projectId].results;
    const removedResult = results.find((item) => item.id === videoId);
    // 不存在不执行
    if (!removedResult) {
      throw new Error("Video not found");
    }
    // 旧数据用于回退
    const originalResults = [...results];
    // 乐观更新前端删除
    const updatedResults = results.filter((item) => item.id !== videoId);
    // 立即更新本地状态
    this.updateResults(updatedResults, projectId);
    try {
      await videoDelete(videoId);
    } catch (error) {
      console.error(`Failed to delete video ${videoId}:`, error);
      // 恢复原始数据
      this.updateResults(originalResults, projectId);
      // 继续抛出异常
      throw error;
    }
  }

  // 添加临时结果
  private addTempResult(projectId: string, result: VideoResult) {
    this.init(projectId);
    const tempKey = this.generateTempKey(
      result.inputText,
      result.voice.id,
      result.actor.id
    );
    this.projectCache[projectId] = {
      ...this.projectCache[projectId],
      tempResults: {
        ...this.projectCache[projectId].tempResults,
        [tempKey]: result,
      },
    };
    this.dispatchResultsChange(projectId);
    this.save();
  }

  // 清空临时结果
  private clearTempResults(projectId: string): void {
    this.init(projectId);
    this.projectCache[projectId].tempResults = {};
    this.dispatchResultsChange(projectId);
    this.save();
  }

  // ==================== 生成管理 ====================

  // 生成视频
  public async generateVideos(
    projectId: string,
    folderOriginId: string
  ): Promise<void> {
    const cache = this.projectCache[projectId];
    if (!cache || !cache.text || !cache.actors.length) {
      throw new Error("Missing text or actors");
    }
    // 旧数据用于回退
    const originalText = cache.text;
    const originalActors = [...cache.actors];
    // 清除输入
    this.updateText("", projectId);
    this.updateActors([], projectId);
    // 乐观更新，添加临时结果
    originalActors.forEach((actor) => {
      const selectAudio = actor.selectAudio || {
        audio: actor.audio,
        playbackRate: 1,
      };
      this.addTempResult(projectId, {
        actor: {
          actorModelId: actor.modelId,
          id: actor.id,
          imageUrl: actor.image,
          name: actor.name,
        },
        compressedVideoUrl: "",
        id: "",
        inputText: originalText,
        taskId: "",
        videoStatus: 1,
        videoUrl: "",
        voice: {
          audioUrl: "",
          id: selectAudio.audio.id,
          modelName: selectAudio.audio.modelName,
          name: "",
          speed: 0,
          voiceTtsId: selectAudio.audio.voiceTtsId,
          ageGroup: selectAudio.audio.ageGroup,
          language: selectAudio.audio.language,
          gender: selectAudio.audio.gender,
        },
      });
    });
    try {
      // 尝试生成视频
      const response = await videoAudioVideoProcessBatch({
        tasks: originalActors.map((actor) => {
          const selectAudio = actor.selectAudio || {
            audio: actor.audio,
            playbackRate: 1,
          };
          return {
            folderId: folderOriginId,
            projectId: projectId,
            text: originalText,
            actorId: actor.id,
            sceneId: actor.modelId,
            voiceId: selectAudio.audio.voiceTtsId,
            voiceModelName: selectAudio.audio.modelName,
            voiceSettings: {
              stability: 0.5,
              similarityBoost: 0.75,
              styleExaggeration: 0.0,
              speed: selectAudio.playbackRate,
            },
          };
        }),
      });
      const mappedResults: VideoResult[] = response.videos.map((result) => ({
        actor: {
          actorModelId: result.actor.actorModelId,
          id: result.actor.id,
          imageUrl: result.actor.imageUrl,
          name: result.actor.name,
        },
        compressedVideoUrl: result.compressedVideoUrl,
        id: result.id,
        inputText: result.inputText,
        processResult: "",
        taskId: result.taskId,
        videoStatus: result.videoStatus,
        videoUrl: result.videoUrl,
        voice: {
          audioUrl: result.voice.audioUrl,
          id: result.voice.id,
          modelName: result.voice.modelName,
          name: result.voice.name,
          speed: result.voice.speed,
          voiceTtsId: result.voice.voiceTtsId,
          ageGroup: result.voice.ageGroup,
          language: result.voice.language,
          gender: result.voice.gender,
          // emotion: result.voice.emotion,
        },
      }));
      // 清除临时结果
      this.clearTempResults(projectId);
      // 更新结果
      this.addResults(mappedResults, projectId);
    } catch (error) {
      // 回退输入
      this.updateText(originalText, projectId);
      this.updateActors(originalActors, projectId);
      // 无论是否成功，都清除临时结果
      this.clearTempResults(projectId);
      // 继续抛出异常
      throw error;
    }
  }

  // 重新生成视频
  public async regenerateVideo(
    projectId: string,
    video: VideoResult
  ): Promise<void> {
    const { actor, inputText, voice } = video;
    this.addTempResult(projectId, {
      actor: {
        actorModelId: actor.actorModelId,
        id: actor.id,
        imageUrl: actor.imageUrl,
        name: actor.name,
      },
      compressedVideoUrl: "",
      id: "",
      inputText,
      taskId: "",
      videoStatus: 1,
      videoUrl: "",
      voice: {
        audioUrl: "",
        id: voice.id,
        modelName: voice.modelName,
        name: "",
        speed: 0,
        voiceTtsId: voice.voiceTtsId,
        ageGroup: voice.ageGroup,
        language: voice.language,
        gender: voice.gender,
      },
    });
    try {
      await videoRetryFailedVideo({
        failedVideoId: video.id,
      });
      // 清除临时结果
      this.clearTempResults(projectId);
    } catch (error) {
      // 无论是否成功，都清除临时结果
      this.clearTempResults(projectId);
      // 继续抛出异常
      throw error;
    }
  }

  // ==================== 数据获取和轮询 ====================

  // 完整数据获取
  public async fetchResults(projectId: string): Promise<void> {
    if (!projectId) {
      console.warn("projectId is required");
      return;
    }
    try {
      // 尝试获取视频列表
      const response = await videoVideosByFolderProject({
        projectId: projectId,
        currentPage: 1,
        pageSize: 1000,
      });
      const results = response.videos || [];
      const mappedResults: VideoResult[] = results.map((result) =>
        result.isUnusual
          ? ({
            actor: {},
            voice: {},
            id: result.id,
            isUnusual: true,
          } as any)
          : {
            actor: {
              actorModelId: result.actor.actorModelId,
              id: result.actor.id,
              imageUrl: result.actor.imageUrl,
              name: result.actor.name,
            },
            compressedVideoUrl: result.compressedVideoUrl,
            id: result.id,
            inputText: result.inputText,
            processResult: "",
            taskId: result.taskId,
            videoStatus: result.videoStatus,
            videoUrl: result.videoUrl,
            voice: {
              audioUrl: result.voice.audioUrl,
              id: result.voice.id,
              modelName: result.voice.modelName,
              name: result.voice.name,
              speed: result.voice.speed,
              voiceTtsId: result.voice.voiceTtsId,
              ageGroup: result.voice.ageGroup,
              language: result.voice.language,
              gender: result.voice.gender,
              // emotion: result.voice.emotion,
            },
          }
      );
      // 检查是否有生成中的视频 如果有需要进行单独的任务轮询
      for (const result of mappedResults) {
        // 状态是生成中且taskId有值才需要轮询
        if (result.videoStatus === 1 && result.taskId) {
          this.generatorPolling(result.taskId);
        }
      }
      // 如果没有taskId为空的任务则停止轮询 否则继续轮询
      if (mappedResults.every((item) => item.taskId)) {
        this.stopPolling();
      } else {
        this.startPolling(projectId);
      }
      // 移除临时数据并更新数据
      this.clearTempResults(projectId);
      this.updateResults(mappedResults, projectId);
    } catch (error) {
      // 无论是否成功 都清除临时结果
      this.clearTempResults(projectId);
      this.updateResults([], projectId);
      // 继续抛出异常
      throw error;
    }
  }

  // 单独轮询 用于获取生成视频结果
  public generatorPolling(taskId: string): () => void {
    // 已在轮询则清除原轮询函数
    if (this.generatorPollingMap[taskId]) {
      clearTimeout(this.generatorPollingMap[taskId]);
    }
    this.generatorPollingMap[taskId] = setTimeout(async () => {
      try {
        const newResult: VideoResult = await videoCallbackResult({ taskId });
        // 同步结果
        for (const [projectId, cache] of Object.entries(this.projectCache)) {
          if (cache) {
            const existingResults = cache.results || [];
            const resultIndex = existingResults.findIndex(
              (result) => result.taskId === newResult.taskId
            );
            if (resultIndex !== -1) {
              // 更新结果列表
              const updatedResults = [...existingResults];
              updatedResults[resultIndex] = newResult;
              // 更新数据
              this.updateResults(updatedResults, projectId);
              // 是否需要继续轮询
              newResult.videoStatus === 1
                ? this.generatorPolling(taskId)
                : this.stopGeneratorPolling(taskId);
              return;
            }
          }
        }
      } catch {
        // 发生错误仍需继续轮询
        this.generatorPolling(taskId);
      }
      // 在列表中找不到相应的数据则停止不必要的轮询
      this.stopGeneratorPolling(taskId);
    }, 10000);
    return () => this.stopGeneratorPolling(taskId);
  }

  // 停止某个单独轮询
  public stopGeneratorPolling(taskId: string) {
    if (this.generatorPollingMap[taskId]) {
      clearTimeout(this.generatorPollingMap[taskId]);
      delete this.generatorPollingMap[taskId];
    }
  }

  public startPolling(projectId: string): () => void {
    this.polling = setTimeout(async () => {
      try {
        await this.fetchResults(projectId);
      } catch (error) {
        // 发生错误仍需继续轮询
        this.startPolling(projectId);
      }
    }, 10000);

    return () => this.stopPolling();
  }

  public stopPolling(): void {
    if (this.polling) {
      clearTimeout(this.polling);
      this.polling = null;
    }
  }

  public stopAllPolling() {
    this.stopPolling();
    for (const taskId of Object.keys(this.generatorPollingMap)) {
      this.stopGeneratorPolling(taskId);
    }
  }

  // ==================== Remix功能 ====================

  public remixFromResult(videoResult: VideoResult, projectId: string): void {
    const talkingActor: TalkingActor = {
      id: videoResult.actor.id,
      modelId: videoResult.actor.actorModelId,
      name: videoResult.actor.name,
      image: videoResult.actor.imageUrl,
      video: "",
      tags: [],
      isSelected: true,
      isFavorite: false,
      isMyActor: false,
      isPro: false,
      isHD: false,
      audio: {
        id: videoResult.voice.id,
        voiceTtsId: videoResult.voice.voiceTtsId,
        modelName: videoResult.voice.modelName,
        name: videoResult.voice.name,
        speed: videoResult.voice.speed,
        audioUrl: videoResult.voice.audioUrl,
        ageGroup: videoResult.voice.ageGroup,
        language: videoResult.voice.language,
        gender: videoResult.voice.gender,
        // emotion: videoResult.voice.emotion,
        isStarred: false,
      },
    };

    this.updateText(videoResult.inputText, projectId);
    this.updateActors([talkingActor], projectId);
  }

  // ==================== 事件和监听 ====================
  public onScriptChange(
    projectId: string,
    listener: (e: VideoCreationScriptChangeEvent) => void
  ) {
    if (canUseWindow()) {
      window.addEventListener(
        `${this.STORAGE_KEY}:${projectId}:scriptChange`,
        listener
      );
      return () => {
        window.removeEventListener(
          `${this.STORAGE_KEY}:${projectId}:scriptChange`,
          listener
        );
      };
    }
  }

  private dispatchScriptChange(projectId: string): void {
    if (canUseWindow()) {
      const event = new CustomEvent<VideoCreationScriptChangeEventDetail>(
        `${this.STORAGE_KEY}:${projectId}:scriptChange`,
        {
          detail: {
            projectId,
            text: this.getText(projectId),
          },
        }
      );

      window.dispatchEvent(event);
    }
  }

  public onActorsChange(
    projectId: string,
    listener: (e: VideoCreationActorsChangeEvent) => void
  ) {
    if (canUseWindow()) {
      window.addEventListener(
        `${this.STORAGE_KEY}:${projectId}:actorsChange`,
        listener
      );
      return () => {
        window.removeEventListener(
          `${this.STORAGE_KEY}:${projectId}:actorsChange`,
          listener
        );
      };
    }
  }

  private dispatchActorsChange(projectId: string): void {
    if (canUseWindow()) {
      const event = new CustomEvent<VideoCreationActorsChangeEventDetail>(
        `${this.STORAGE_KEY}:${projectId}:actorsChange`,
        {
          detail: {
            projectId,
            actors: this.getActors(projectId),
          },
        }
      );

      window.dispatchEvent(event);
    }
  }

  public onResultsChange(
    projectId: string,
    listener: (e: VideoCreationResultsChangeEvent) => void
  ) {
    if (canUseWindow()) {
      window.addEventListener(
        `${this.STORAGE_KEY}:${projectId}:resultsChange`,
        listener
      );
      return () => {
        window.removeEventListener(
          `${this.STORAGE_KEY}:${projectId}:resultsChange`,
          listener
        );
      };
    }
  }

  private dispatchResultsChange(projectId: string): void {
    if (canUseWindow()) {
      const event = new CustomEvent<VideoCreationResultsChangeEventDetail>(
        `${this.STORAGE_KEY}:${projectId}:resultsChange`,
        {
          detail: {
            projectId,
            results: this.getResults(projectId),
          },
        }
      );

      window.dispatchEvent(event);
    }
  }

  // ==================== 其他方法 ====================

  // 生成临时数据key
  private generateTempKey(
    inputText: string,
    voiceId: string,
    actorId: string
  ): string {
    return `${inputText}|${voiceId}|${actorId}|${uuid()}`;
  }

  private init(projectId: string): void {
    if (!projectId) {
      throw new Error("projectId is required");
    }
    if (!this.projectCache[projectId]) {
      this.projectCache[projectId] = {
        text: "",
        actors: [],
        results: [],
        tempResults: {},
      };
    }
  }

  private load(): void {
    this.projectCache = {};
    if (canUseLocalStorage()) {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          this.projectCache = parsed || {};
        }
      } catch (error) {
        console.error("Failed to load video creation cache:", error);
        // 如果加载失败，保持空对象
        this.projectCache = {};
      }
    }
  }

  private save(): void {
    if (canUseLocalStorage()) {
      try {
        const json = JSON.stringify(this.projectCache);
        localStorage.setItem(this.STORAGE_KEY, json);
      } catch (error) {
        console.error("Failed to save video creation cache:", error);
      }
    }
  }

  /**
   * 清除所有缓存数据
   * 用于退出登录时清理用户数据
   */
  public clear(): void {
    // 停止所有轮询
    this.stopPolling();
    Object.keys(this.generatorPollingMap).forEach((taskId) => {
      this.stopGeneratorPolling(taskId);
    });
    // 清空内存缓存
    this.projectCache = {};
    this.polling = null;
    this.generatorPollingMap = {};
    // 删除本地存储
    if (canUseLocalStorage()) {
      try {
        localStorage.removeItem(this.STORAGE_KEY);
      } catch (error) {
        console.error("Failed to clear video creation cache:", error);
      }
    }
  }
}

// ==================== React Hooks ====================

// 脚本
export const useVideoCreationScript = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();
  const [script, setScript] = useState("");
  const { debounceEstimate, spendCredits } = subscriptionHook.useFeatureEstimate();

  useEffect(() => {
    return creationVideoManager.onScriptChange(activeProjectOriginId, (e) => {
      setScript(e.detail.text);
    });
  }, [activeProjectOriginId]);

  useEffect(() => {
    if (activeProjectOriginId) {
      const projectData = creationVideoManager.getText(activeProjectOriginId);
      // 初始化时，获取项目脚本并计算消耗积分
      if (projectData) {
        debounceEstimate({
          event: SPEND_CREDITS_EVENT.ai_oral_broadcast_video,
          count: calculateCharacters(projectData)
        });
      }
      setScript(projectData);
    }
  }, [activeProjectOriginId]);

  const update = (script: string) => {
    if (activeProjectOriginId) {
      creationVideoManager.updateText(script, activeProjectOriginId);
      // 获取预估消耗积分
      debounceEstimate({
        event: SPEND_CREDITS_EVENT.ai_oral_broadcast_video,
        count: calculateCharacters(script)
      });
    }
  };

  const verify = () => script && script.length >= 100;

  return { script, update, verify, spendCredits };
};

// 演员列表
export const useVideoCreationActors = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();
  const [actors, setActors] = useState<TalkingActor[]>([]);

  useEffect(() => {
    return creationVideoManager.onActorsChange(activeProjectOriginId, (e) => {
      setActors(e.detail.actors);
    });
  }, [activeProjectOriginId]);

  useEffect(() => {
    if (activeProjectOriginId) {
      const projectData = creationVideoManager.getActors(activeProjectOriginId);
      setActors(projectData);
    }
  }, [activeProjectOriginId]);

  const update = (actors: TalkingActor[]) => {
    if (activeProjectOriginId) {
      creationVideoManager.updateActors(actors, activeProjectOriginId);
    }
  };

  const verify = () => actors?.length > 0;

  return { actors, update, verify };
};

// 视频结果列表
export const useVideoCreationResults = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();
  const [results, setResults] = useState<VideoResult[]>([]);
  const [loading, setLoading] = useState(false);
  const { showToastByCode } = useToast();

  // 监听结果变化
  useEffect(() => {
    return creationVideoManager.onResultsChange(activeProjectOriginId, (e) => {
      setResults(e.detail.results);
    });
  }, [activeProjectOriginId]);

  // 立即获取结果
  useEffect(() => {
    if (activeProjectOriginId) {
      const projectData = creationVideoManager.getResults(
        activeProjectOriginId
      );
      setResults(projectData);
    }
  }, [activeProjectOriginId]);

  // 初始化后台请求一次，确保数据同步
  useEffect(() => {
    if (activeProjectOriginId) {
      init();
    }
  }, [activeProjectOriginId]);

  const init = async () => {
    setLoading(true);
    try {
      await creationVideoManager.fetchResults(activeProjectOriginId);
    } catch (error) {
      showToastByCode(error);
    }
    setLoading(false);
  };

  return { loading, results };
};

// 生成视频
export const useVideoCreationGenerator = () => {
  const { activeProjectOriginId, activeFolderOriginId } =
    creationProductHooks.useActiveProjectOriginId();
  const { script, verify: verifyScript } = useVideoCreationScript();
  const { actors, verify: verifyActors } = useVideoCreationActors();
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const generator = async () => {
    if (loading) {
      return;
    }
    analytics.trackCreationClickGenerate({
      actors: actors.map((actor) => ({
        actorId: actor.id,
        source: actor.source,
        voiceId: actor.selectAudio
          ? actor.selectAudio.audio.id
          : actor.audio.id,
      })),
      script: script,
    });
    if (!verifyScript() || !verifyActors()) {
      analytics.trackCreationClickGenerateFail({
        reason: "script or actors not verify",
        actors: actors.map((actor) => ({
          actorId: actor.id,
          voiceId: actor.selectAudio
            ? actor.selectAudio.audio.id
            : actor.audio.id,
        })),
        script: script,
      });
      return;
    }
    setLoading(true);
    try {
      await creationVideoManager.generateVideos(
        activeProjectOriginId,
        activeFolderOriginId
      );
      // 开始轮询
      creationVideoManager.startPolling(activeProjectOriginId);
    } catch (error) {
      analytics.trackCreationClickGenerateFail({
        reason: error.message,
        actors: actors.map((actor) => ({
          actorId: actor.id,
          voiceId: actor.selectAudio
            ? actor.selectAudio.audio.id
            : actor.audio.id,
        })),
        script: script,
      });
      // 特殊错误码不处理 继续抛出
      if (error.code === 1020) {
        setLoading(false);
        throw error;
      }
      showToastByCode(error);
    }
    analytics.trackCreationClickGenerateSuccess({
      actors: actors.map((actor) => ({
        actorId: actor.id,
        voiceId: actor.selectAudio
          ? actor.selectAudio.audio.id
          : actor.audio.id,
      })),
      script: script,
    });
    setLoading(false);
  };

  return { loading, generator };
};

// 生成视频
export const useVideoCreationRegenerator = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();
  const { showToastByCode } = useToast();

  const regenerator = async (video: VideoResult) => {
    try {
      await creationVideoManager.regenerateVideo(activeProjectOriginId, video);
      // 开始轮询
      creationVideoManager.startPolling(activeProjectOriginId);
    } catch (error) {
      showToastByCode(error);
    }
  };

  return { regenerator };
};

// 重新生成
export const useVideoCreationRemix = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();

  const remix = (videoResult: VideoResult) => {
    if (activeProjectOriginId) {
      creationVideoManager.remixFromResult(videoResult, activeProjectOriginId);
    }
  };

  return { remix };
};

// 删除
export const useVideoCreationDeleteResult = () => {
  const { activeProjectOriginId } =
    creationProductHooks.useActiveProjectOriginId();

  const remove = (videoResult: VideoResult) => {
    if (activeProjectOriginId) {
      creationVideoManager.removeResult(activeProjectOriginId, videoResult.id);
    }
  };

  return { remove };
};

// 停止轮询的钩子
export const useVideoCreationPollingCleanup = () => {
  useEffect(() => {
    // 返回清理函数，在组件卸载时执行
    return () => {
      // 停止项目轮询
      creationVideoManager.stopAllPolling();
    };
  }, []);
};

// ==================== 导出 ====================

// 更新 creationVideoHooks 导出
export const creationVideoHooks = {
  useScript: useVideoCreationScript,
  useActors: useVideoCreationActors,
  useResults: useVideoCreationResults,
  useGenerator: useVideoCreationGenerator,
  useRegenerator: useVideoCreationRegenerator,
  useRemix: useVideoCreationRemix,
  useDeleteResult: useVideoCreationDeleteResult,
  usePollingCleanup: useVideoCreationPollingCleanup,
};

export const creationVideoManager = CreationVideoManager.getInstance();
